'use client'

import React from 'react'
import 'animate.css/animate.min.css'
import { AnimationOnScroll } from 'react-animation-on-scroll'
import Image from 'next/image'

import maleSvg from './icons/male.svg'
import femaleSvg from './icons/female.svg'

import { VictoryStack, VictoryLabel, VictoryBar, VictoryAxis } from 'victory'
import { useLocale } from 'next-intl'
import type { Locale } from '@/i18n'

type AgeStructureBoxProps = {
  ageStructureData: any
  animationProps: any
  highlightColor?: string
  lightColor?: string
}

const AgeStructureBox = ({
  ageStructureData,
  animationProps,
  highlightColor = '#266782',
  lightColor = '#aadbd7',
}: AgeStructureBoxProps) => {
  const locale = useLocale() as Locale
  const width = 500
  const height = 400
  const padding = { top: 60, bottom: 60, left: 10, right: 10 }

  return (
    <AnimationOnScroll
      {...animationProps}
      className="flex flex-1 flex-col border border-solid text-white"
      style={{ borderColor: highlightColor, scrollSnapAlign: 'center' }}
    >
      <div
        className="ml-6 mt-6 text-left text-3xl font-medium leading-8"
        style={{ color: highlightColor }}
      >
        {ageStructureData['pyramid1'][locale]}
        <br />
        {ageStructureData['pyramid2'][locale]}
      </div>
      <div className="relative flex flex-1 items-center justify-center">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', height: 'auto', marginBottom: '-40px' }}
        >
          <VictoryAxis
            standalone={false}
            height={height}
            width={width}
            padding={padding}
            style={{
              axis: { stroke: 'none' },
              ticks: { stroke: 'none' },
              tickLabels: { fontSize: 12, fill: '#555' },
            }}
            offsetX={30}
            orientation={'left'}
            tickLabelComponent={
              <VictoryLabel x={40} lineHeight={20} textAnchor="start" />
            }
            tickValues={ageStructureData['men']
              ?.map((point: any) => point.x)
              .reverse()}
          />
          <VictoryStack
            horizontal
            standalone={false}
            // setting a symmetric domain makes it much easier to center the axis
            domain={{ y: [-80, 80] }}
            padding={padding}
            height={height}
            width={width}
            style={{
              data: { width: 15 },
              labels: {
                fontSize: 16,
                color: highlightColor,
                fontFamily: 'var(--font-walsheim)',
              },
            }}
          >
            <VictoryBar
              style={{ data: { fill: highlightColor } }}
              data={ageStructureData['men']}
              y={(data: any) => -Math.abs(data.y)}
              labels={({ datum }: any) => `${Math.abs(datum.y)}`}
            />
            <VictoryBar
              style={{ data: { fill: lightColor } }}
              data={ageStructureData['women']}
              labels={({ datum }: any) => `${Math.abs(datum.y)}`}
            />
          </VictoryStack>
        </svg>
        <div className="gender-icons text-gray-dimmed absolute left-0 top-[24px] flex w-full">
          <div className="male-icon absolute right-[50%] top-0">
            <Image
              src={maleSvg}
              alt="male"
              style={{
                fill: '#777',
                color: '#555',
                height: '34px',
                opacity: '0.5',
              }}
            />
          </div>
          <div className="female-icon absolute left-[50%] top-0">
            <Image
              src={femaleSvg}
              alt="male"
              style={{
                fill: '#777',
                color: '#555',
                height: '34px',
                opacity: '0.5',
              }}
            />
          </div>
        </div>
      </div>
      <span
        className="mt-6 w-full whitespace-pre-line px-6 pb-4 uppercase sm:mt-2"
        style={{ color: highlightColor }}
      >
        {ageStructureData['title'][locale]}
      </span>
    </AnimationOnScroll>
  )
}
export default AgeStructureBox
